import React, { Component } from "react";
import moment from "moment";
import Bridge from "../../../utils/Bridge";
import { Spin, Tabs } from "antd";
import { Chart, registerables } from "chart.js";
import { Line } from "react-chartjs-2";

Chart.register(...registerables);

export default class Respiratory extends Component {
  constructor(props) {
    super(props);
    this.state = {
      records: [],
      loading: false,
    };
  }

  componentDidMount() {
    this.getInClinicSessionList();
  }

  getInClinicSessionList = () => {
    this.setState({ loading: true });
    let params = "?eals_id=" + this.props.eals_id;
    Bridge.personal_data_viewer.le_study
      .get_le_study_in_clinic_session_list(params)
      .then((response) => {
        if (response.status == "1") {
          this.setState({ records: response.data, loading: false }, () => {
            this.getGreatestPcfTrialChartData();
          });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch((error) => {
        this.setState({ loading: false });
      });
  };
  getGreatestPcfTrialChartData = () => {
    let displayData = this.state.records.length > 0 ? this.state.records : [];

    // Extract data for graph - dates and greatest PCF trial values
    const chartData = displayData
      .filter((item) => item.greatestPCF && item.greatestPCF)
      .map((item) => ({
        date: item.entry_date
          ? moment(item.entry_date).format("MM/DD/YY")
          : null,
        value: item.greatestPCF,
      }))
      .filter((item) => item.date !== null)
      .sort(
        (a, b) =>
          moment(a.date, "MM/DD/YY").unix() - moment(b.date, "MM/DD/YY").unix()
      );

    // Calculate min and max values for y-axis
    const values = chartData.map((item) => item.value);
    let yMin = 0;
    let yMax = 500; // Default max when no data

    if (values.length > 0) {
      const dataMin = Math.min(...values);
      const dataMax = Math.max(...values);
      // Add padding: 10% below min (but not less than 0) and 10% above max
      yMin = Math.max(0, dataMin - (dataMax - dataMin) * 0.1);
      yMax = dataMax + (dataMax - dataMin) * 0.1;
      // If max is less than 500, keep it at 500. Otherwise round up to nearest 50
      if (yMax <= 500) {
        yMax = 500;
      } else {
        yMax = Math.ceil(yMax / 50) * 50; // Round up to nearest 50
      }
    }

    return {
      labels: chartData.map((item) => item.date),
      datasets: [
        {
          label: "Greatest PCF Trial (l/min)",
          data: chartData.map((item) => item.value),
          borderColor: "#007bff",
          backgroundColor: "#007bff",
          borderWidth: 2,
          pointRadius: 8,
          pointBackgroundColor: "#007bff",
          pointBorderColor: "#000",
          pointBorderWidth: 1.5,
          pointHoverRadius: 10,
          pointHoverBackgroundColor: "#007bff",
          pointHoverBorderColor: "#000",
          tension: 0,
          spanGaps: false,
        },
      ],
      yAxisMin: yMin,
      yAxisMax: yMax,
    };
  };

  getFvcPercentPredictedChartData = () => {
    const displayData = this.state.records.length > 0 ? this.state.records : [];

    // Extract data for graph - dates and FVC % Predicted values
    const chartData = displayData
      .filter(
        (item) => item.greatestFVC !== undefined && item.greatestFVC !== null
      )
      .map((item) => ({
        date:
          item.sessionDate ||
          (item.entry_date ? moment(item.entry_date).format("MM/DD/YY") : null),
        value: item.greatestFVC,
      }))
      .filter(
        (item) =>
          item.date !== null && item.value !== undefined && item.value !== null
      )
      .sort(
        (a, b) =>
          moment(a.date, "MM/DD/YY").unix() - moment(b.date, "MM/DD/YY").unix()
      );

    // Calculate min and max values for y-axis
    const values = chartData.map((item) => item.value);
    let yMin = 0;
    let yMax = 120; // Default max when no data (120%)

    if (values.length > 0) {
      const dataMin = Math.min(...values);
      const dataMax = Math.max(...values);
      // Add padding: 10% below min (but not less than 0) and 10% above max
      yMin = Math.max(0, dataMin - (dataMax - dataMin) * 0.1);
      yMax = dataMax + (dataMax - dataMin) * 0.1;
      // If max is less than 120, keep it at 120. Otherwise round up to nearest 10
      if (yMax <= 120) {
        yMax = 120;
      } else {
        yMax = Math.ceil(yMax / 10) * 10; // Round up to nearest 10
      }
    }

    return {
      labels: chartData.map((item) => item.date),
      datasets: [
        {
          label: "FVC % Predicted",
          data: chartData.map((item) => item.value),
          borderColor: "#007bff",
          backgroundColor: "#007bff",
          borderWidth: 2,
          pointRadius: 8,
          pointBackgroundColor: "#007bff",
          pointBorderColor: "#000",
          pointBorderWidth: 1.5,
          pointHoverRadius: 10,
          pointHoverBackgroundColor: "#007bff",
          pointHoverBorderColor: "#000",
          tension: 0,
          spanGaps: false,
        },
      ],
      yAxisMin: yMin,
      yAxisMax: yMax,
    };
  };

  getGreatestForcedVitalCapacityChartData = () => {
    const displayData = this.state.records.length > 0 ? this.state.records : [];

    // Extract data for graph - dates and greatest forced vital capacity values (using raw L)
    const chartData = displayData
      .filter(
        (item) =>
          item.greatestFVCRawL !== undefined && item.greatestFVCRawL !== null
      )
      .map((item) => ({
        date:
          item.sessionDate ||
          (item.entry_date ? moment(item.entry_date).format("MM/DD/YY") : null),
        value: item.greatestFVC,
        rawML: item.greatestFVCRaw,
        rawL: item.greatestFVCRawL,
      }))
      .filter(
        (item) =>
          item.date !== null && item.rawL !== undefined && item.rawL !== null
      )
      .sort(
        (a, b) =>
          moment(a.date, "MM/DD/YY").unix() - moment(b.date, "MM/DD/YY").unix()
      );

    // Calculate min and max values for y-axis based on rawL
    // Convert rawL to numbers (handle string values)
    const values = chartData
      .map((item) => {
        const rawL = item.rawL;
        let numValue;
        // Convert to number, handling string values
        if (typeof rawL === "string") {
          numValue = parseFloat(rawL);
        } else {
          numValue = Number(rawL);
        }
        // Replace empty, null, undefined, or NaN values with 0
        if (rawL === "" || rawL === null || rawL === undefined || isNaN(numValue)) {
          return 0;
        }
        return numValue;
      })
      .filter((val) => !isNaN(val));

    let yMin = 0;
    let yMax = 6; // Default max when no data (6 L)

    if (values.length > 0) {
      const dataMin = Math.min(...values);
      const dataMax = Math.max(...values);

      // Handle case where all values are the same (min === max)
      if (dataMin === dataMax) {
        if (dataMax < 2) {
          yMin = 0;
          yMax = Math.max(1.0, dataMax + 0.2);
          yMax = Math.ceil(yMax / 0.1) * 0.1; // Round up to nearest 0.1
        } else {
          // For larger single values, add padding
          yMin = Math.max(0, dataMin - dataMin * 0.1);
          yMax = dataMax + dataMax * 0.1;
          yMax = Math.ceil(yMax / 0.5) * 0.5; // Round up to nearest 0.5
        }
      } else {
        // Exception for smaller values: if max is less than 2, always start from 0
        if (dataMax < 2) {
          yMin = 0;
          // Add padding above max
          const padding = Math.max(0.2, dataMax * 0.2);
          yMax = dataMax + padding;
          yMax = Math.ceil(yMax / 0.1) * 0.1; // Round up to nearest 0.1
          // Ensure we have at least 1.0 range for proper tick display (similar to PCF's 500 minimum)
          if (yMax < 1.0) {
            yMax = 1.0;
          }
        } else {
          // Add padding: 10% below min (but not less than 0) and 10% above max
          yMin = Math.max(0, dataMin - (dataMax - dataMin) * 0.1);
          yMax = dataMax + (dataMax - dataMin) * 0.1;
          // If max is less than 6, keep it at 6. Otherwise round up to nearest 0.5
          if (yMax <= 6) {
            yMax = 6;
          } else {
            yMax = Math.ceil(yMax / 0.5) * 0.5; // Round up to nearest 0.5
          }
        }
      }
    }

    // Calculate tick count for small values
    let tickCount = undefined;
    if (values.length > 0 && Math.max(...values) < 2) {
      const step = 0.1;
      tickCount = Math.round((yMax - yMin) / step) + 1;
    }

    return {
      labels: chartData.map((item) => item.date),
      datasets: [
        {
          label: "Greatest Forced Vital Capacity",
          data: chartData.map((item) => {
            const rawL = item.rawL;
            let numValue;
            // Convert to number for chart data
            if (typeof rawL === "string") {
              numValue = parseFloat(rawL);
            } else {
              numValue = Number(rawL);
            }
            // Replace empty, null, undefined, or NaN values with 0
            if (rawL === "" || rawL === null || rawL === undefined || isNaN(numValue)) {
              return 0;
            }
            return numValue;
          }),
          borderColor: "#007bff",
          backgroundColor: "#007bff",
          borderWidth: 2,
          pointRadius: 8,
          pointBackgroundColor: "#007bff",
          pointBorderColor: "#000",
          pointBorderWidth: 1.5,
          pointHoverRadius: 10,
          pointHoverBackgroundColor: "#007bff",
          pointHoverBorderColor: "#000",
          tension: 0,
          spanGaps: false,
        },
      ],
      yAxisMin: yMin,
      yAxisMax: yMax,
      isSmallValue: values.length > 0 ? Math.max(...values) < 2 : false,
      tickCount: tickCount,
    };
  };
  render() {
    const formData = this.props.language;
    const greatestPcfChartData = this.getGreatestPcfTrialChartData();
    const fvcPercentPredictedChartData = this.getFvcPercentPredictedChartData();
    const greatestFvcChartData = this.getGreatestForcedVitalCapacityChartData();
    const displayData = this.state.records;
    const tableContent = (
      <table>
        <tr>
          <th>
            {formData.page.personalDataViewer.respiratory.columns.sessionDate}
          </th>
          <th>
            {formData.page.personalDataViewer.respiratory.columns.pcfTrial}
          </th>
          <th>
            {
              formData.page.personalDataViewer.respiratory.columns
                .greatestPcfTrial
            }
          </th>
          <th>
            {
              formData.page.personalDataViewer.respiratory.columns
                .forcedVitalCapacity
            }
          </th>
          <th>
            {
              formData.page.personalDataViewer.respiratory.columns
                .greatestForcedVitalCapacity
            }
          </th>
        </tr>
        {displayData &&
          displayData.map((item, index) => {
            return (
              <tr key={index}>
                <td>
                  {item.sessionDate ||
                    (item.entry_date
                      ? moment(item.entry_date).format("MM/DD/YY")
                      : "---")}
                </td>
                <td>
                  <div
                    style={{
                      lineHeight: "1.6",
                      textAlign: "left",
                      paddingLeft: "10px",
                    }}
                  >
                    <div key={item.in_clinic_session_id}>
                      {formData.page.personalDataViewer.respiratory.words.trial}{" "}
                      1: {item.pcf1} | (l/
                      {
                        formData.page.personalDataViewer.respiratory.words.min
                      }) <br />
                      {
                        formData.page.personalDataViewer.respiratory.words.trial
                      }{" "}
                      2: {item.pcf2} | (l/
                      {
                        formData.page.personalDataViewer.respiratory.words.min
                      }) <br />
                      {
                        formData.page.personalDataViewer.respiratory.words.trial
                      }{" "}
                      3: {item.pcf3} | (l/
                      {
                        formData.page.personalDataViewer.respiratory.words.min
                      }) <br />
                    </div>
                  </div>
                </td>
                <td>
                  {item.greatestPCF
                    ? `${formData.page.personalDataViewer.respiratory.words.trial} : ${item.greatestPCF} (l/${formData.page.personalDataViewer.respiratory.words.min})`
                    : "---"}
                </td>
                <td>
                  <div style={{ textAlign: "center", lineHeight: "1.6" }}>
                    <div key={item.in_clinic_session_id}>
                      <b>
                        {
                          formData.page.personalDataViewer.respiratory.words
                            .trial
                        }{" "}
                        1:
                      </b>{" "}
                      {item.fvc1 ? item.fvc1 : "---"}%{" "}
                      {
                        formData.page.personalDataViewer.respiratory.words
                          .predicted
                      }{" "}
                      | &nbsp;
                      {item.fvc1Raw ? item.fvc1Raw : "---"}{" "}
                      {formData.page.personalDataViewer.respiratory.words.raw}{" "}
                      mL | &nbsp;
                      {item.fvc1RawL ? item.fvc1RawL : "---"}{" "}
                      {formData.page.personalDataViewer.respiratory.words.raw} L{" "}
                      <br />
                      <b>
                        {
                          formData.page.personalDataViewer.respiratory.words
                            .trial
                        }{" "}
                        2:
                      </b>{" "}
                      {item.fvc2 ? item.fvc2 : "---"}%{" "}
                      {
                        formData.page.personalDataViewer.respiratory.words
                          .predicted
                      }{" "}
                      | &nbsp;
                      {item.fvc2Raw ? item.fvc2Raw : "---"}{" "}
                      {formData.page.personalDataViewer.respiratory.words.raw}{" "}
                      mL | &nbsp;
                      {item.fvc2RawL ? item.fvc2RawL : "---"}{" "}
                      {formData.page.personalDataViewer.respiratory.words.raw} L
                      <br />
                      <b>
                        {
                          formData.page.personalDataViewer.respiratory.words
                            .trial
                        }{" "}
                        3:
                      </b>{" "}
                      {item.fvc3 ? item.fvc3 : "---"}%{" "}
                      {
                        formData.page.personalDataViewer.respiratory.words
                          .predicted
                      }{" "}
                      | &nbsp;
                      {item.fvc3Raw ? item.fvc3Raw : "---"}{" "}
                      {formData.page.personalDataViewer.respiratory.words.raw}{" "}
                      mL | &nbsp;
                      {item.fvc3RawL ? item.fvc3RawL : "---"}{" "}
                      {formData.page.personalDataViewer.respiratory.words.raw} L
                    </div>
                  </div>
                </td>
                <td>
                  <div style={{ textAlign: "center", lineHeight: "1.6" }}>
                    <div>
                      <b>
                        {
                          formData.page.personalDataViewer.respiratory.words
                            .trial
                        }
                        :
                      </b>{" "}
                      {item.greatestFVC ? item.greatestFVC : "---"} (%{" "}
                      {
                        formData.page.personalDataViewer.respiratory.words
                          .predicted
                      }
                      ) | {item.greatestFVCRaw ? item.greatestFVCRaw : "---"} (
                      {formData.page.personalDataViewer.respiratory.words.raw}{" "}
                      mL) |{" "}
                      {item.greatestFVCRawL ? item.greatestFVCRawL : "---"} (
                      {formData.page.personalDataViewer.respiratory.words.raw}{" "}
                      L)
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        {displayData.length === 0 && (
          <tr>
            <td colSpan={5}>{this.props.language.page.notAvailable}</td>
          </tr>
        )}
      </table>
    );
    const tabItems = [
      {
        key: "1",
        label:
          formData.page.personalDataViewer.respiratory
            .respiratorySessionDetails,
        children: <div className="respiratory-tab-content">{tableContent}</div>,
      },
      {
        key: "2",
        label:
          formData.page.personalDataViewer.respiratory.fvcPercentPredicted
            .title,
        children: (
          <div
            className="respiratory-tab-content respiratory-chart-container"
            style={{ minHeight: "500px" }}
          >
            <Line
              key="fvc-percent-predicted-chart"
              data={fvcPercentPredictedChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                  padding: {
                    left: 15,
                    right: 15,
                    top: 15,
                    bottom: 15,
                  },
                },
                plugins: {
                  legend: {
                    display: false,
                  },
                  title: {
                    display: true,
                    font: {
                      size: 16,
                      weight: "bold",
                    },
                    color: "#000",
                    padding: {
                      top: 10,
                      bottom: 20,
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    min: fvcPercentPredictedChartData.yAxisMin,
                    max: fvcPercentPredictedChartData.yAxisMax,
                    title: {
                      display: true,
                      text:
                        formData.page.personalDataViewer.respiratory
                          .fvcPercentPredicted.charts.fvcPercentPredicted +
                        " (%)",
                      font: {
                        size: 12,
                        weight: "bold",
                      },
                      color: "#000",
                    },
                    ticks: {
                      color: "#000",
                      stepSize: 10,
                    },
                    grid: {
                      color: "rgba(0, 0, 0, 0.1)",
                    },
                    offset: true,
                    afterFit: function (scale) {
                      scale.paddingTop = 20;
                      scale.paddingBottom = 20;
                      scale.paddingRight = 10;
                    },
                  },
                  x: {
                    title: {
                      display: true,
                      text: formData.page.personalDataViewer.respiratory
                        .fvcPercentPredicted.charts.sessionDate,
                      font: {
                        size: 12,
                        weight: "bold",
                      },
                      color: "#000",
                    },
                    ticks: {
                      color: "#000",
                      maxTicksLimit: 10,
                    },
                    grid: {
                      display: false,
                    },
                    offset: true,
                    afterFit: function (scale) {
                      scale.paddingLeft = 20;
                      scale.paddingRight = 20;
                    },
                  },
                },
              }}
            />
          </div>
        ),
      },
      {
        key: "3",
        label:
          formData.page.personalDataViewer.respiratory.greatestPcfTrialChart
            .title,
        children: (
          <div
            className="respiratory-tab-content respiratory-chart-container"
            style={{ minHeight: "500px" }}
          >
            <Line
              key="pcf-chart"
              data={greatestPcfChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                  padding: {
                    left: 15,
                    right: 15,
                    top: 15,
                    bottom: 15,
                  },
                },
                plugins: {
                  legend: {
                    display: false,
                  },
                  title: {
                    display: true,
                    // text: "Greatest PCF Trial",
                    font: {
                      size: 16,
                      weight: "bold",
                    },
                    color: "#000",
                    padding: {
                      top: 10,
                      bottom: 20,
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    min: greatestPcfChartData.yAxisMin,
                    max: greatestPcfChartData.yAxisMax,
                    title: {
                      display: true,
                      text:
                        formData.page.personalDataViewer.respiratory
                          .greatestPcfTrialChart.charts.greatestPcfTrial +
                        " (L/" +
                        formData.page.personalDataViewer.respiratory.words.min +
                        ")",
                      font: {
                        size: 12,
                        weight: "bold",
                      },
                      color: "#000",
                    },
                    ticks: {
                      color: "#000",
                      stepSize: 50,
                    },
                    grid: {
                      color: "rgba(0, 0, 0, 0.1)",
                    },
                    offset: true,
                    afterFit: function (scale) {
                      scale.paddingTop = 20;
                      scale.paddingBottom = 20;
                      scale.paddingRight = 10;
                    },
                  },
                  x: {
                    title: {
                      display: true,
                      text: formData.page.personalDataViewer.respiratory
                        .greatestPcfTrialChart.charts.sessionDate,
                      font: {
                        size: 12,
                        weight: "bold",
                      },
                      color: "#000",
                    },
                    ticks: {
                      color: "#000",
                      maxTicksLimit: 10,
                    },
                    grid: {
                      display: false,
                    },
                    offset: true,
                    afterFit: function (scale) {
                      scale.paddingLeft = 20;
                      scale.paddingRight = 20;
                    },
                  },
                },
              }}
            />
          </div>
        ),
      },
      {
        key: "4",
        label:
          formData.page.personalDataViewer.respiratory
            .greatestForcedVitalCapacity.title,
        children: (
          <div
            className="respiratory-tab-content respiratory-chart-container"
            style={{ position: "relative", minHeight: "500px" }}
          >
            <Line
              key="fvc-chart"
              data={greatestFvcChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                  padding: {
                    left: 15,
                    right: 15,
                    top: 15,
                    bottom: 15,
                  },
                },
                plugins: {
                  legend: {
                    display: false,
                  },
                  title: {
                    display: true,
                    text:
                      "(" +
                      formData.page.personalDataViewer.respiratory
                        .greatestForcedVitalCapacity.charts.raw +
                      " L)",
                    font: {
                      size: 16,
                      weight: "bold",
                    },
                    color: "#000",
                    padding: {
                      top: 10,
                      bottom: 20,
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    min: greatestFvcChartData.yAxisMin,
                    max: greatestFvcChartData.yAxisMax,
                    title: {
                      display: true,
                      text: formData.page.personalDataViewer.respiratory
                        .greatestForcedVitalCapacity.charts
                        .greatestForcedVitalCapacity,
                      font: {
                        size: 12,
                        weight: "bold",
                      },
                      color: "#000",
                    },
                    ticks: {
                      color: "#000",
                      stepSize: greatestFvcChartData.isSmallValue ? 0.1 : 0.5,
                      precision: 1,
                      ...(greatestFvcChartData.isSmallValue && {
                        autoSkip: false,
                      }),
                    },
                    grid: {
                      color: "rgba(0, 0, 0, 0.1)",
                    },
                    offset: true,
                    afterFit: function (scale) {
                      scale.paddingTop = 20;
                      scale.paddingBottom = 20;
                      scale.paddingRight = 10;
                      // Reduce left padding for small values to minimize space
                      if (greatestFvcChartData.isSmallValue) {
                        scale.paddingLeft = 0;
                      }
                    },
                  },
                  x: {
                    title: {
                      display: true,
                      text: formData.page.personalDataViewer.respiratory
                        .greatestForcedVitalCapacity.charts.sessionDate,
                      font: {
                        size: 12,
                        weight: "bold",
                      },
                      color: "#000",
                    },
                    ticks: {
                      color: "#000",
                      maxTicksLimit: 10,
                    },
                    grid: {
                      display: false,
                    },
                    offset: true,
                    afterFit: function (scale) {
                      scale.paddingLeft = 20;
                      scale.paddingRight = 20;
                    },
                  },
                },
              }}
            />
          </div>
        ),
      },
    ];
    return (
      <div className="als_frs_sessions">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 px-0">
              <Spin spinning={this.state.loading}>
                <Tabs
                  className="respiratory-tabs"
                  items={tabItems}
                  defaultActiveKey="1"
                />
              </Spin>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
